import * as THREE from 'three';
import { NET, TOOLS, TORCH } from '../config';
import { CRAFTABLE_IDS, type CraftableId, type CraftedDoorId } from '../game/craftables';
import type { ItemId } from '../game/inventory';
import type { ServerInfo, Account } from '../net';
import type { Backend } from './backend';
import type { WorldConnection } from './connection';
import { DOOR_IDS, WINDOW_ITEM_IDS, type LocalBackend, type LocalDeps } from './localBackend';
import { noteSeenPlayer } from '../game/seenPlayers';
import { NODE_RESPAWN_SEC } from '../game/nodes';
import type { JoinInfo, InputCommand, ActionIntent, ServerEvent, Snapshot, WelcomeInfo, WorldOp } from './protocol';

// NetBackend (spec section 19, Phase M1): the networked authority seam over the World Durable Object.
// Your OWN actions still apply through the wrapped LocalBackend the instant you act (prediction: the
// game feels identical to single-player), and the shared-world side effect of each action is relayed
// through the DO as a WorldOp so every other client applies the same mutation -- a picked mushroom
// disappears for everyone. On join, the DO's welcome carries the shared clock, the weather seed, and
// the persisted build/deploy/claim op log, which is replayed so a late joiner sees the world as built.
//
// FAIL-SOFT: if the DO cannot be reached within NET.connectTimeoutMs, connect() logs a warning and the
// backend degrades to plain LocalBackend behaviour (single-player) -- nothing else in the game changes.
//
// M1 limits (deliberate, M2 lands stable ids): world entities are matched cross-client by POSITION,
// so an op on an entity the receiving client has not generated identically is skipped fail-soft; loot
// scattered by a remote salvage/gather exists only on the acting client until stable drop ids arrive.
export class NetBackend implements Backend {
  readonly isLocal = false;
  // Fired when the server clock arrives (join + periodic): main syncs day-night/weather off it.
  onWorldClock: (elapsedMs: number, lockedTimeFrac: number | null) => void = () => {};
  // RESYNC hooks (reconnect after a blip). Start fires before the missed ops are applied -- main clears the
  // remote-player roster so ghosts from the gap can't linger (audit E4); done fires after the catch-up is
  // applied -- main unfreezes input and hides the "resyncing" overlay.
  onResyncStart: () => void = () => {};
  onResynced: () => void = () => {};
  // A station's server state from the welcome/resync roster (live changes ride conn.onStationState instead).
  onStation: (cid: string, on: boolean, lit: boolean) => void = () => {};

  private timeOffsetMs = 0; // serverNow - performance.now()
  private clockTimer = 0;
  private lastPlaceRef: unknown = null; // identity of builder.lastPlaced we already broadcast
  private applyingRemote = false; // true while applying another player's op (suppresses re-broadcast of drops it spawns)
  // High-water mark of the persisted op log THIS client has applied (live broadcasts, our own acked ops, and
  // replay/catch-up). A reconnect welcome applies only ops above it -- never a double-apply, never a hole.
  private lastAppliedSeq = -1;
  // True once connect()'s initial replay finished. A welcome arriving DURING the join (a blip in the
  // connect/ping window) must not resync a world that hasn't replayed yet -- it is stashed and applied
  // right after the join completes instead (otherwise the whole log double-applies).
  private joined = false;
  private pendingResync: WelcomeInfo | null = null;
  private readonly cbs: Array<(e: ServerEvent) => void> = [];
  // Remote build ops that did not resolve HERE on first apply -- almost always because their support (a wall under a
  // ceiling/roof) has not arrived on this client yet (out-of-order live delivery, or a prior op lost to a blip).
  // We retry them whenever another piece lands, instead of silently dropping them -- the fix for "her roof showed
  // for her but vanished for me until she relogged". Bounded tries; a relog's ordered replay is the final backstop.
  private deferredBuilds: Array<{ op: Extract<WorldOp, { k: 'buildAt' }>; tries: number }> = [];
  private static readonly MAX_DEFER_TRIES = 12;

  constructor(
    private readonly inner: LocalBackend,
    private readonly conn: WorldConnection,
    private readonly deps: LocalDeps,
    private readonly localName: string,
  ) {
    this.conn.onOp = (actor, op, seq) => {
      try { this.applyRemote(actor, op); } catch (err) { console.warn('[oxide net] remote op failed', op, err); }
      if (seq !== undefined && seq > this.lastAppliedSeq) this.lastAppliedSeq = seq;
    };
    // Our OWN persisted ops come back as acks (the broadcast excludes the sender), so the high-water mark must
    // advance off the ack seq -- otherwise a resync would re-apply our own builds onto us (double deploys).
    this.conn.onOpAck = (seq) => { if (seq > this.lastAppliedSeq) this.lastAppliedSeq = seq; };
    this.conn.onOpNack = (reason, op) => console.warn('[oxide net] op refused by authority', reason, op);
    this.conn.onResync = (w) => this.resync(w);
    this.conn.onClock = (timeMs, elapsedMs, lockedTimeFrac) => {
      this.timeOffsetMs = timeMs - performance.now();
      this.onWorldClock(elapsedMs, lockedTimeFrac);
    };
    // Every LOCAL drop (a toss, or loot scattered by a barrel smash / harvest / corpse chop) broadcasts
    // itself at its resting spot, so the SAME items land on every screen. Guarded so a drop we spawned
    // FROM a remote op is not echoed back. This is the fix for "I smash a barrel and no items appear".
    // NOTE: none of these mirror callbacks check `connected` any more (audit A2: those gates ran BEFORE sendOp,
    // so an op created during a blip was dropped before it could ever queue -- the reliability layer was dead
    // code). sendOp now parks every op in the unacked ring and the reconnect resends it; NetBackend only exists
    // in MP, so there is no single-player path to spam.
    this.deps.dropped.onSpawn = (id, amount, x, z, yaw) => {
      if (this.applyingRemote) return;
      this.conn.sendOp({ k: 'dropAt', item: id, n: amount, x, z, yaw });
    };
    // A barrel / junk pile smashed to nothing -> persist a despawn (the SAME crateAt op crates use on empty), so it
    // stays gone on relog + for late joiners. Guarded so a barrel a REMOTE op just depleted here is not re-broadcast.
    this.deps.assets.onDestroyed = (x, z) => {
      if (this.applyingRemote) return;
      this.conn.sendOp({ k: 'crateAt', x, z });
    };
    // A tree chopped down / rock mined out -> persist the fell (felledAt) so it stays down on relog + for late joiners
    // and respawns same-spot on the shared clock. Guarded so a node a REMOTE op just felled here is not re-broadcast.
    this.deps.nodeField.onFelled = (x, z) => {
      if (this.applyingRemote) return;
      this.conn.sendOp({ k: 'felledAt', x, z });
    };
    // Door open/close + lock/unlock/code, addressed by the leaf hinge. Toggle relays live so her open == my open;
    // lock/unlock/code persist so a late joiner sees the lock + owner. Guarded so a remote-applied door op is not echoed.
    this.deps.builder.onDoorToggle = (x, y, z, open) => { if (this.applyingRemote) return; this.conn.sendOp({ k: 'doorToggleAt', x, y, z, open }); };
    this.deps.builder.onDoorLock = (x, y, z, kind, code) => { if (this.applyingRemote) return; this.conn.sendOp({ k: 'doorLockAt', x, y, z, kind, code }); };
    this.deps.builder.onDoorUnlock = (x, y, z) => { if (this.applyingRemote) return; this.conn.sendOp({ k: 'doorUnlockAt', x, y, z }); };
    this.deps.builder.onDoorCode = (x, y, z, code) => { if (this.applyingRemote) return; this.conn.sendOp({ k: 'doorCodeAt', x, y, z, code }); };
    this.deps.builder.onDoorGrant = (x, y, z, user) => { if (this.applyingRemote) return; this.conn.sendOp({ k: 'doorGrantAt', x, y, z, user }); };
    // A chest got a lock: broadcast + persist it so the lock renders for everyone (fixes "she can't see the lock")
    // and the DO can enforce a wooden lock's owner+claim access. Guarded against echoing a remote-applied lock.
    this.deps.chests.onLock = (x, y, z, kind, code) => { if (this.applyingRemote) return; this.conn.sendOp({ k: 'containerLockAt', x, y, z, kind, code }); };
  }

  async connect(server: ServerInfo, account: Account): Promise<JoinInfo> {
    const joinLocal = await this.inner.connect(server, account); // seed spawn point + kit locally; world STATE comes from the DO
    // ONLINE-ONLY: a failed or unplayable connect THROWS so the caller boots back to the menu with a reason.
    // There is NO single-player fallback -- the client must never silently run a stale, unshared local world.
    let w;
    try {
      w = await this.conn.connectAsync(NET.connectTimeoutMs); // rejects on timeout / closed socket / kick
    } catch (err) {
      // A kick during the join carries the REAL reason (e.g. "Server is full") -- surface it; only the
      // plain timeout collapses to the generic unreachable message.
      const msg = err instanceof Error ? err.message : '';
      throw new Error(msg && !/timed out/i.test(msg) ? msg : 'Could not reach the server. It may be down or your connection dropped. Try again in a moment.');
    }
    // Reject an unplayable-ping join before entering the world (spec: reject on high ping). A 99999 read means
    // the ping ITSELF never returned (the socket died mid-join) -- that's an unreachable server, not high ping.
    const ping = await this.conn.measurePing(3000);
    if (ping > NET.maxPingMs) {
      this.conn.disconnect();
      throw new Error(ping >= 99999 ? 'Could not reach the server. It may be down or your connection dropped. Try again in a moment.' : `Ping too high (${ping} ms). Pick a server closer to you.`);
    }
    this.timeOffsetMs = w.timeMs - performance.now();
    // Replay the world's persisted construction history so this client's world matches what the
    // players before us built. Ops are ordered; each one fails soft if it no longer resolves.
    for (const rec of w.ops) {
      try { this.applyRemote(rec.actor, rec.op); } catch (err) { console.warn('[oxide net] rehydrate op failed', rec.op, err); }
      if (rec.seq !== undefined && rec.seq > this.lastAppliedSeq) this.lastAppliedSeq = rec.seq;
    }
    this.retryDeferredBuilds(); // resolve any piece whose support arrived later in the ordered log
    // Re-apply every world object that is still gone at join (barrels/crates + picked plants), each with its shared
    // remaining respawn time, so a barrel someone broke or a plant someone picked stays gone for us + respawns at the
    // same moment as everyone else -- instead of replaying intact (position-keyed removed sets, not the op log).
    for (const s of w.salvaged ?? []) { try { this.deps.assets.markSalvagedAt(s.x, s.z, s.rem); } catch { /* streamed out / already gone */ } }
    for (const f of w.foraged ?? []) { try { this.deps.forage.markPickedAt(f.x, f.z, f.rem); } catch { /* streamed out / already gone */ } }
    for (const t of w.felled ?? []) { try { this.deps.nodeField.markFelledAt(t.x, t.z, t.rem); } catch { /* streamed out / already gone */ } }
    // Loot still lying on the ground (persisted drops, audit F8): spawn each at its recorded resting spot WITH
    // its remaining despawn seconds, so a late joiner's drop blinks out at the same real moment as everyone
    // else's instead of restarting the 10-minute clock. applyingRemote guard: mirrors, not re-tosses.
    this.applyingRemote = true;
    try { for (const dr of w.drops ?? []) { try { this.deps.dropped.spawnRemote(dr.item as ItemId, dr.n, dr.x, dr.z, dr.yaw, dr.rem); } catch { /* bad row */ } } }
    finally { this.applyingRemote = false; }
    for (const s of w.stations ?? []) this.onStation(s.cid, s.on, s.lit); // fires lit before we joined render lit
    // Periodic drift check against the server's world clock (piggybacks the one live socket).
    this.clockTimer = window.setInterval(() => this.conn.requestClock(), NET.clockSyncSec * 1000);
    this.onWorldClock(w.elapsedMs, w.lockedTimeFrac);
    this.joined = true;
    // A blip during the join window stashed its welcome (see resync): apply the catch-up now that the world exists.
    if (this.pendingResync) { const w2 = this.pendingResync; this.pendingResync = null; this.resync(w2); }
    return { ...joinLocal, serverTimeMs: w.timeMs, worldElapsedMs: w.elapsedMs, lockedTimeFrac: w.lockedTimeFrac, weatherSeed: w.weatherSeed };
  }

  // RECONNECT CATCH-UP: the server re-sends the full welcome on every (re)join; the initial join consumed the
  // first one above, and every later one lands here. Apply exactly the persisted ops this client missed during
  // the gap (seq > lastApplied), refresh the removed-object sets (idempotent re-marks), and re-anchor the clock.
  // main clears the remote roster via onResyncStart (the welcome's sleeper list + post-hello states rebuild it)
  // and unfreezes input via onResynced.
  private resync(w: WelcomeInfo): void {
    if (!this.joined) { this.pendingResync = w; return; } // mid-join blip: the world hasn't replayed yet (see connect)
    this.onResyncStart();
    this.timeOffsetMs = w.timeMs - performance.now();
    let applied = 0;
    for (const rec of w.ops) {
      if (rec.seq === undefined || rec.seq <= this.lastAppliedSeq) continue;
      // OUR OWN ops already applied locally at action time (prediction); if the ack for one was lost in the blip
      // we never advanced lastAppliedSeq for it, and re-applying it here would double the deploy on our screen.
      // Advance the mark and skip -- the unacked ring's resend gets it re-acked (server ck-dedupe) separately.
      if (rec.actor === this.localName) { this.lastAppliedSeq = rec.seq; continue; }
      try { this.applyRemote(rec.actor, rec.op); applied++; } catch (err) { console.warn('[oxide net] resync op failed', rec.op, err); }
      this.lastAppliedSeq = rec.seq;
    }
    this.retryDeferredBuilds();
    for (const s of w.salvaged ?? []) { try { this.deps.assets.markSalvagedAt(s.x, s.z, s.rem); } catch { /* gone */ } }
    for (const f of w.foraged ?? []) { try { this.deps.forage.markPickedAt(f.x, f.z, f.rem); } catch { /* gone */ } }
    for (const t of w.felled ?? []) { try { this.deps.nodeField.markFelledAt(t.x, t.z, t.rem); } catch { /* gone */ } }
    // Reconcile ground drops against the server list: remove settled ghosts (loot someone picked while we were
    // out -- "picking it up" again would dupe), add drops we missed. Recent/airborne drops are left alone: our
    // own blip-time tosses are still in the unacked ring and must not be culled before their dropAt lands.
    this.applyingRemote = true;
    try { this.deps.dropped.reconcileRemote(w.drops ?? []); } finally { this.applyingRemote = false; }
    for (const s of w.stations ?? []) this.onStation(s.cid, s.on, s.lit); // fires lit/died during the blip
    if (applied) console.info(`[oxide net] resynced: applied ${applied} missed op(s)`);
    this.onWorldClock(w.elapsedMs, w.lockedTimeFrac);
    this.onResynced();
  }

  disconnect(): void {
    if (this.clockTimer) { window.clearInterval(this.clockTimer); this.clockTimer = 0; }
    this.conn.disconnect();
    this.inner.disconnect();
  }

  now(): number { return performance.now() + this.timeOffsetMs; }

  // M1 movement is presence-relayed (conn.sendPose at NET.sendHz from the main loop) and your own
  // player is simulated locally, so there is no command stream yet. Server-sim reconciliation is M2+.
  sendInput(_cmd: InputCommand): void { /* presence relay covers M1 movement */ }

  action(intent: ActionIntent): void {
    // The shared-world op is derived BEFORE the local apply where it reads pre-state (a pickup needs the
    // drop's position while the drop still exists), and AFTER it where success decides (placeBuild only
    // broadcasts a placement that actually committed). No `connected` gate here: sendOp parks the op in the
    // unacked ring and the reconnect delivers it (the old gate silently ate blip-time actions; audit A2).
    const pre = this.opFor(intent);
    this.inner.action(intent); // prediction: your own client applies immediately, always
    if (intent.t === 'placeBuild') {
      const p = this.deps.builder.lastPlaced;
      if (p && p !== this.lastPlaceRef) { // identity change = this action's placement committed
        this.lastPlaceRef = p;
        this.conn.sendOp({ k: 'buildAt', piece: p.piece, tier: p.tier, x: p.x, y: p.y, z: p.z, yaw: p.yaw, sign: p.sign, raised: p.raised });
      }
      return;
    }
    if (pre) this.conn.sendOp(pre);
  }

  onEvent(cb: (e: ServerEvent) => void): () => void {
    const un = this.inner.onEvent(cb); // local applies emit through the wrapped backend
    this.cbs.push(cb);                 // remote applies emit through us (e.g. 'built' for grass refresh)
    return () => { un(); const i = this.cbs.indexOf(cb); if (i >= 0) this.cbs.splice(i, 1); };
  }

  onSnapshot(_cb: (s: Snapshot) => void): () => void { return () => {}; } // server-sim snapshots are M2+

  private emit(e: ServerEvent): void { for (const cb of this.cbs) cb(e); }

  // Map an intent to the WorldOp other clients need to converge. Local-only intents (eat, craft,
  // vitals, respawn, tool repair) return null: they touch nothing another client can see yet.
  private opFor(intent: ActionIntent): WorldOp | null {
    const d = this.deps;
    switch (intent.t) {
      case 'forage': {
        const p = d.forage.posAt(intent.index);
        return p ? { k: 'forageAt', x: p.x, z: p.z } : null;
      }
      case 'pickup': {
        const peek = d.dropped.peekById(intent.dropId);
        const p = d.dropped.posById(intent.dropId);
        return peek && p ? { k: 'pickupAt', x: p.x, z: p.z, item: peek.id } : null;
      }
      case 'drop':
        return null; // the toss's drop broadcasts itself via dropped.onSpawn (uniform with all loot scatter)
      case 'gather': {
        const node = d.nodeField.nodeById(intent.nodeId);
        if (!node) return null;
        const tf = TOOLS[intent.tool];
        const mult = tf ? (node.spotKind === 'tree' ? tf.tree : tf.ore) : 1;
        return { k: 'gatherAt', x: node.pos.x, z: node.pos.z, mult, hit: intent.hit, cam: intent.cam };
      }
      case 'salvage': {
        const p = d.assets.posAt(intent.index);
        return p ? { k: 'salvageAt', x: p.x, z: p.z, hits: intent.hits ?? (intent.tool === 'torch' ? TORCH.smashHits : 1) } : null;
      }
      case 'crate': // a crate someone emptied -> tell everyone to despawn it (contents are already synced by seed)
        return { k: 'crateAt', x: intent.x, z: intent.z };
      case 'chopCorpse': {
        const p = d.corpses.posById(intent.corpseId);
        return p ? { k: 'chopAt', x: p.x, z: p.z } : null;
      }
      case 'deploy':
        return { k: 'deployAt', item: intent.item, x: intent.x, y: intent.y, z: intent.z, yaw: intent.yaw, sign: intent.sign, host: intent.host };
      // Resolve the aim RAY to the exact piece struck NOW (before the local apply removes/retiers it) and
      // broadcast that piece's world centre. Every other client edits the piece at that key -- no re-raycast,
      // so the wall demolished here is the same wall gone everywhere (the wrong-piece + not-synced fix). No
      // piece under the cursor -> null (nothing to relay; the local apply was a no-op too).
      case 'demolish': {
        const k = d.builder.resolvePieceAt(intent.ox, intent.oy, intent.oz, intent.dx, intent.dy, intent.dz);
        return k ? { k: 'demolishAt', x: k.x, y: k.y, z: k.z } : null;
      }
      case 'upgrade': {
        const k = d.builder.resolvePieceAt(intent.ox, intent.oy, intent.oz, intent.dx, intent.dy, intent.dz);
        return k ? { k: 'upgradeTo', tier: intent.tier, x: k.x, y: k.y, z: k.z } : null;
      }
      case 'repairBuild': {
        const k = d.builder.resolvePieceAt(intent.ox, intent.oy, intent.oz, intent.dx, intent.dy, intent.dz);
        return k ? { k: 'repairAt', x: k.x, y: k.y, z: k.z } : null;
      }
      case 'claimOwn': return { k: 'claimOp', op: 'own', x: intent.x, z: intent.z };
      case 'claimClear': return { k: 'claimOp', op: 'clear', x: intent.x, z: intent.z };
      case 'claimAddUser': return { k: 'claimOp', op: 'add', x: intent.x, z: intent.z, user: intent.user };
      case 'claimRemoveUser': return { k: 'claimOp', op: 'remove', x: intent.x, z: intent.z, user: intent.user };
      case 'bedRename': return { k: 'bedNameAt', x: intent.x, y: intent.y, z: intent.z, name: intent.name };
      case 'chestRename': return { k: 'chestNameAt', x: intent.x, y: intent.y, z: intent.z, name: intent.name };
      case 'bedGive': return { k: 'bedGiveAt', x: intent.x, y: intent.y, z: intent.z, owner: intent.owner };
      case 'bedPickup': return { k: 'bedRemoveAt', x: intent.x, y: intent.y, z: intent.z };
      case 'deployPickup': return { k: 'deployRemoveAt', x: intent.x, y: intent.y, z: intent.z };
      default: return null;
    }
  }

  // Apply ANOTHER player's world op to our copy of the shared world. Mutates world state only -- never
  // this player's inventory or vitals (the actor's client granted the yield to the actor). Every match
  // is by position and fails soft when the entity is missing here (streamed out / already gone).
  private applyRemote(actor: string, op: WorldOp): void {
    const d = this.deps;
    noteSeenPlayer(actor); // remember every player whose ops we see, for the add-user / give pickers
    this.applyingRemote = true; // any drop this op spawns is a mirror, not a new local drop -> do not re-broadcast
    try { this.applyRemoteInner(actor, op, d); } finally { this.applyingRemote = false; }
  }

  private applyRemoteInner(actor: string, op: WorldOp, d: LocalDeps): void {
    switch (op.k) {
      case 'forageAt': {
        const i = d.forage.nearestPickable(op.x, op.z, NET.matchRadius);
        if (i >= 0) d.forage.collect(i); // yield discarded: the actor got it
        break;
      }
      case 'pickupAt':
        d.dropped.takeNearOf(op.x, op.z, op.item, NET.dropMatchRadius);
        break;
      case 'dropAt':
        d.dropped.spawnRemote(op.item, op.n, op.x, op.z, op.yaw); // settled at the exact broadcast spot so pickups match
        break;
      case 'gatherAt': {
        const n = d.nodeField.nearestAlive(op.x, op.z, NET.matchRadius);
        if (n) {
          const hit = op.hit ? new THREE.Vector3(op.hit.x, op.hit.y, op.hit.z) : null;
          d.nodeField.harvest(n.node, hit, new THREE.Vector3(op.cam.x, op.cam.y, op.cam.z), op.mult);
        }
        break;
      }
      case 'salvageAt': {
        const i = d.assets.nearestDestroy(op.x, op.z, NET.matchRadius);
        if (i >= 0) {
          d.assets.jolt(i);
          d.assets.salvage(i, op.hits); // no loot scatter here: cross-client drops need M2 stable ids
        }
        break;
      }
      case 'felledAt': // someone fully chopped a tree / mined a rock -> fell that exact node here too + share-clock respawn
        d.nodeField.markFelledAt(op.x, op.z, NODE_RESPAWN_SEC);
        break;
      case 'crateAt': // someone smashed a barrel / emptied a crate -> mark that exact prop salvaged here too (any method)
        d.assets.markSalvagedAt(op.x, op.z);
        break;
      case 'chopAt': {
        const c = d.corpses.nearest(new THREE.Vector3(op.x, 0, op.z), NET.matchRadius);
        if (c) d.corpses.chopById(c.id);
        break;
      }
      case 'deployAt':
        // Mirrors LocalBackend.deploy minus the actor-side gates (inventory + claim were checked there). Pass
        // the ACTOR as the owner so a remote-placed door/claim/chest is owned by its real placer on this client
        // (not by us) -- this is what makes locks hold + claim add-user work across clients.
        if (DOOR_IDS.has(op.item)) {
          d.builder.placeCraftedDoor(op.x, op.y, op.z, op.yaw, op.item as CraftedDoorId, op.sign ?? 1, actor);
          this.emit({ t: 'built' });
        } else if (CRAFTABLE_IDS.has(op.item as CraftableId)) {
          d.deployables.deploy(op.item as CraftableId, op.x, op.y, op.z, op.yaw, op.host, actor);
          if (WINDOW_ITEM_IDS.has(op.item)) d.builder.markWindowOccupied(op.x, op.y, op.z);
        } else if (op.item === 'campfire') d.campfires.deploy(new THREE.Vector3(op.x, op.y, op.z), op.yaw);
        else d.chests.deploy(op.item === 'bigchest' ? 'big' : 'small', new THREE.Vector3(op.x, op.y, op.z), op.yaw, actor);
        break;
      case 'buildAt':
        this.applyBuildAt(op);
        break;
      // Position-keyed (current) OR the legacy ray shape (old persisted ops from before the switch) -- detect by
      // which fields are present so replaying an existing world's history still resolves the right piece.
      case 'demolishAt': {
        const lg = op as unknown as { ox?: number; oy?: number; oz?: number; dx?: number; dy?: number; dz?: number };
        const ok = typeof op.x === 'number'
          ? d.builder.applyNetDemolishAt(op.x, op.y, op.z)
          : typeof lg.ox === 'number' && d.builder.applyNetDemolish(lg.ox, lg.oy ?? 0, lg.oz ?? 0, lg.dx ?? 0, lg.dy ?? 0, lg.dz ?? 0);
        if (ok) this.emit({ t: 'built' });
        break;
      }
      case 'upgradeTo': {
        const lg = op as unknown as { ox?: number; oy?: number; oz?: number; dx?: number; dy?: number; dz?: number };
        if (typeof op.x === 'number') d.builder.applyNetUpgradeAt(op.tier, op.x, op.y, op.z);
        else if (typeof lg.ox === 'number') d.builder.applyNetUpgrade(op.tier, lg.ox, lg.oy ?? 0, lg.oz ?? 0, lg.dx ?? 0, lg.dy ?? 0, lg.dz ?? 0);
        break;
      }
      case 'repairAt': {
        const lg = op as unknown as { ox?: number; oy?: number; oz?: number; dx?: number; dy?: number; dz?: number };
        if (typeof op.x === 'number') d.builder.applyNetRepairAt(op.x, op.y, op.z);
        else if (typeof lg.ox === 'number') d.builder.applyNetRepair(lg.ox, lg.oy ?? 0, lg.oz ?? 0, lg.dx ?? 0, lg.dy ?? 0, lg.dz ?? 0);
        break;
      }
      case 'claimOp': {
        const cl = d.deployables.claimByPos(op.x, op.z);
        if (!cl) break;
        const who = actor || this.localName;
        if (op.op === 'own') d.deployables.claimTakeOwnership(cl, who);
        else if (op.op === 'clear') d.deployables.claimClearOwnership(cl, who);
        else if (op.op === 'add' && op.user) d.deployables.claimAddUser(cl, who, op.user);
        else if (op.op === 'remove' && op.user) d.deployables.claimRemoveUser(cl, who, op.user);
        break;
      }
      // Sleeping-bag rename / give / pickup -- addressed by the bag's world spot (see deployables.*BedAt). The
      // owner already applied locally (prediction); these replay it on every other client so names, respawn
      // ownership and pickups converge. A late joiner replays them after the deployAt that created the bag.
      case 'bedNameAt': d.deployables.renameBedAt(op.x, op.z, op.name); break;
      case 'chestNameAt': d.chests.renameAt(op.x, op.y, op.z, op.name); break;
      case 'bedGiveAt': d.deployables.giveBedAt(op.x, op.z, op.owner); break;
      case 'bedRemoveAt': d.deployables.removeBedAt(op.x, op.z); break;
      case 'deployRemoveAt': { // someone hammer-picked-up a placeable: remove it here too (their client granted the item)
        if (!d.deployables.pickupAt(op.x, op.y, op.z) && !d.chests.pickupAt(op.x, op.y, op.z)) d.campfires.pickupAt(op.x, op.y, op.z);
        break;
      }
      // Doors: converge open/close, and lock/unlock/code with the actor as the lock owner (so access gates the same
      // on every client + for late joiners replaying the persisted lock ops).
      case 'doorToggleAt': d.builder.applyDoorToggleAt(op.x, op.y, op.z, op.open); break;
      case 'doorLockAt': d.builder.applyDoorLockAt(op.x, op.y, op.z, op.kind, op.owner ?? actor, op.code); break;
      case 'doorUnlockAt': d.builder.applyDoorUnlockAt(op.x, op.y, op.z); break;
      case 'doorCodeAt': d.builder.applyDoorCodeAt(op.x, op.y, op.z, op.code); break;
      case 'containerLockAt': d.chests.applyLockAt(op.x, op.y, op.z, op.kind, op.owner ?? actor, op.code); break;
      case 'containerGrantAt': d.chests.grantAt(op.x, op.y, op.z, op.user); break; // code-knower recorded everywhere
      case 'doorGrantAt': d.builder.applyDoorGrantAt(op.x, op.y, op.z, op.user); break;
    }
  }

  // Apply a remote build. If it does not resolve here yet (its support has not arrived on this client), DEFER it
  // and retry when later pieces land -- so an out-of-order or briefly-lost wall no longer leaves its ceiling
  // showing for the placer only. A successful place may be the support a deferred piece was waiting on, so it
  // kicks a retry sweep.
  private applyBuildAt(op: Extract<WorldOp, { k: 'buildAt' }>): void {
    if (this.deps.builder.applyNetPlace(op.piece, op.tier, op.x, op.y, op.z, op.yaw, op.sign ?? 1, op.raised ?? false)) {
      this.emit({ t: 'built' });
      this.retryDeferredBuilds();
    } else {
      const k = (o: { piece: string; x: number; y: number; z: number }) => `${o.piece}:${Math.round(o.x * 10)},${Math.round(o.y * 10)},${Math.round(o.z * 10)}`;
      if (!this.deferredBuilds.some((e) => k(e.op) === k(op))) this.deferredBuilds.push({ op, tries: 0 });
    }
  }

  // Re-attempt every deferred build; loop until a pass lands nothing new (so a chain foundation->wall->ceiling
  // resolves in one sweep). Then age each still-stuck entry; drop it past MAX_DEFER_TRIES (its support may never
  // arrive live -- a relog's ordered op-log replay is the backstop). No-op when nothing is deferred (the hot path).
  private retryDeferredBuilds(): void {
    if (!this.deferredBuilds.length) return;
    let landedAny = true;
    while (landedAny && this.deferredBuilds.length) {
      landedAny = false;
      for (let i = this.deferredBuilds.length - 1; i >= 0; i--) {
        const e = this.deferredBuilds[i];
        if (this.deps.builder.applyNetPlace(e.op.piece, e.op.tier, e.op.x, e.op.y, e.op.z, e.op.yaw, e.op.sign ?? 1, e.op.raised ?? false)) {
          this.deferredBuilds.splice(i, 1); this.emit({ t: 'built' }); landedAny = true;
        }
      }
    }
    for (let i = this.deferredBuilds.length - 1; i >= 0; i--) {
      if (++this.deferredBuilds[i].tries >= NetBackend.MAX_DEFER_TRIES) this.deferredBuilds.splice(i, 1);
    }
  }
}
